export const TASK_TYPE_STANDARD = 'standard';
export const TASK_TYPE_NUMERICAL = 'numerical_target';

export const NUMERICAL_CURRENCY_OPTIONS = Object.freeze([
  { code: 'INR', label: 'INR — Indian Rupee (₹)' },
  { code: 'USD', label: 'USD — US Dollar ($)' },
]);

export const DEFAULT_NUMERIC_CONFIG = Object.freeze({
  metric: 'currency',
  currency_code: 'INR',
  target_value: '',
});

export const normalizeTaskType = (value) => (
  value === TASK_TYPE_NUMERICAL ? TASK_TYPE_NUMERICAL : TASK_TYPE_STANDARD
);

export const isNumericalTask = (taskOrType) => {
  const value = typeof taskOrType === 'string'
    ? taskOrType
    : taskOrType?.task_type;
  return normalizeTaskType(value) === TASK_TYPE_NUMERICAL;
};

export const getTaskTypeLabel = (taskOrType) => (
  isNumericalTask(taskOrType) ? 'Numerical Target' : 'Standard'
);

export const getNumericConfig = (task) => ({
  ...DEFAULT_NUMERIC_CONFIG,
  ...(task?.numeric_config || {}),
});

export const normalizeDecimalValue = (value) => {
  if (value === null || value === undefined) return '';
  return String(value).replace(/,/g, '').trim();
};

export const isPositiveDecimal = (value, maximumFractionDigits = 2) => {
  const normalized = normalizeDecimalValue(value);
  const pattern = new RegExp(`^\\d+(?:\\.\\d{1,${maximumFractionDigits}})?$`);
  if (!pattern.test(normalized)) return false;
  return /[1-9]/.test(normalized);
};

export const toNumericInputValue = (value) => {
  if (value === null || value === undefined) return '';
  return String(value);
};

export const formatNumericValue = (value, numericConfig = DEFAULT_NUMERIC_CONFIG) => {
  const normalized = normalizeDecimalValue(value);
  const match = normalized.match(/^(-?)(\d+)(?:\.(\d+))?$/);
  if (!match) return '—';

  const [, sign, rawInteger, rawFraction = ''] = match;
  const integer = rawInteger.replace(/^0+(?=\d)/, '') || '0';
  const metric = numericConfig?.metric || 'currency';
  const currencyCode = String(numericConfig?.currency_code || 'INR').toUpperCase();
  let groupedInteger;
  if (metric === 'currency' && currencyCode !== 'INR') {
    // Preserve the decimal as a string while applying standard 3-digit grouping
    // for USD and future non-INR currencies.
    groupedInteger = integer.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  } else {
    const lastThree = integer.slice(-3);
    const leading = integer.slice(0, -3);
    const groupedLeading = leading.replace(/\B(?=(\d{2})+(?!\d))/g, ',');
    groupedInteger = leading ? `${groupedLeading},${lastThree}` : lastThree;
  }
  const rawFractionLimited = rawFraction.slice(0, 2);
  const fraction = /^0*$/.test(rawFractionLimited)
    ? ''
    : (metric === 'currency' ? rawFractionLimited.padEnd(2, '0') : rawFractionLimited.replace(/0+$/, ''));
  const formatted = `${sign}${groupedInteger}${fraction ? `.${fraction}` : ''}`;
  if (metric === 'currency') {
    const currencySymbols = { INR: '₹', USD: '$', EUR: '€', GBP: '£' };
    return currencySymbols[currencyCode]
      ? `${sign}${currencySymbols[currencyCode]}${formatted.replace(/^-/, '')}`
      : `${currencyCode} ${formatted}`;
  }

  return formatted;
};

export const getTaskProgressPercent = (task) => {
  const value = Number(task?.percent_complete ?? 0);
  return Number.isFinite(value) ? value : 0;
};

export const clampProgressPercent = (value) => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return 0;
  return Math.min(100, Math.max(0, numericValue));
};

export const formatProgressPercent = (value) => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return '0';
  return new Intl.NumberFormat('en-IN', {
    maximumFractionDigits: Number.isInteger(numericValue) ? 0 : 2,
  }).format(numericValue);
};

export const getProgressColorClass = (value) => {
  const percent = clampProgressPercent(value);
  if (percent >= 75) return 'bg-green-500';
  if (percent >= 50) return 'bg-yellow-500';
  if (percent >= 25) return 'bg-orange-500';
  if (percent > 0) return 'bg-red-500';
  return 'bg-muted';
};

export const isProgressEditable = (task, subtaskCount) => {
  if (typeof task?.progress_editable === 'boolean') return task.progress_editable;
  if (isNumericalTask(task)) return false;

  const count = subtaskCount ?? task?.subtask_count ?? 0;
  return Number(count) === 0;
};

export const getNumericalProgressText = (task) => {
  if (!isNumericalTask(task)) return null;
  const config = getNumericConfig(task);
  return `${formatNumericValue(config.current_value ?? 0, config)} / ${formatNumericValue(config.target_value, config)}`;
};

export const getContributionText = (task, numericConfig) => {
  if (!isNumericalTask(task)) return null;
  return formatNumericValue(task?.contribution_value, numericConfig || getNumericConfig(task));
};

export const buildNumericConfigPayload = (numericConfig) => ({
  metric: numericConfig?.metric || 'currency',
  currency_code: String(numericConfig?.currency_code || 'INR').toUpperCase(),
  target_value: normalizeDecimalValue(numericConfig?.target_value),
});

export const buildWeeklyUpdatePayload = (task, data = {}) => {
  const payload = {
    task_id: task?.id,
    notes: data.notes || '',
    blockers: data.blockers || '',
  };

  // Server-calculated progress (numerical targets and Standard parents with
  // subtasks) must not be accepted from a manual reporting surface.
  if (!isNumericalTask(task) && task?.is_ongoing !== true && isProgressEditable(task)) {
    payload.percent_complete = Math.min(100, Math.max(0, parseInt(data.percent_complete, 10) || 0));
  }

  return payload;
};

// Ongoing tasks are continuous responsibilities: no percentage progress, and
// end_date acts as an extendable "Till Date".
export const isOngoingTask = (task) => task?.is_ongoing === true;

export const shouldShowProgress = (task) => !isOngoingTask(task);

export const MAX_DRIVE_LINKS = 10;
const DRIVE_HOSTS = new Set(['drive.google.com', 'docs.google.com']);

export const isGoogleDriveUrl = (value) => {
  if (typeof value !== 'string') return false;
  try {
    const url = new URL(value.trim());
    return url.protocol === 'https:' && DRIVE_HOSTS.has(url.hostname.toLowerCase());
  } catch {
    return false;
  }
};

export const getDriveLinks = (task) => (Array.isArray(task?.drive_links) ? task.drive_links : []);

export const getDriveLinkLabel = (link) => {
  if (link?.label) return link.label;
  try {
    const url = new URL(link.url);
    if (url.hostname === 'docs.google.com') {
      const kind = url.pathname.split('/')[1];
      const names = { document: 'Google Doc', spreadsheets: 'Google Sheet', presentation: 'Google Slides', forms: 'Google Form' };
      return names[kind] || 'Google Docs link';
    }
    return url.pathname.includes('/folders/') ? 'Drive folder' : 'Drive file';
  } catch {
    return 'Drive link';
  }
};
